import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  ArrowLeft, 
  Spinner, 
  Camera,
  CheckCircle,
  Heart,
  User,
  MagnifyingGlass,
  Sparkle,
  ChatCircle,
  HandHeart,
  ShieldCheck
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationForm = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingApp, setExistingApp] = useState(null);
  
  const [formData, setFormData] = useState({
    // STEP 1: Basic Information
    preferred_name: '',
    phone: '',
    height: '',
    occupation: '',
    work_status: '',
    work_status_other: '',
    photo_base64: '',
    coaching_openness: '',
    
    // STEP 2: Relationship Intentions
    relationship_type: [],
    relationship_type_other: '',
    timeline: '',
    personality_traits: [],
    personality_traits_other: '',
    free_time_activities: '',
    three_qualities: ['', '', ''],
    
    // STEP 3: Your Ideal Match
    preferred_genders: [],
    preferred_gender_other: '',
    age_range_min: 25,
    age_range_max: 55,
    must_have_qualities: '',
    deal_breakers: '',
    open_to_different_type: '',
    location_preference: '',
    location_preference_other: '',
    
    // STEP 4: Values & Lifestyle
    shared_values_importance: '',
    social_preference: '',
    lifestyle_preferences: '',
    faith_importance: '',
    religious_affiliation: '',
    religious_affiliation_other: '',
    
    // STEP 5: Readiness & Expectations
    successful_match_definition: '',
    emotionally_available: '',
    open_to_feedback: '',
    anything_else: '',
    
    // Agreement
    agree_confidentiality: false,
    agree_disclaimer: false,
    agree_non_discrimination: false,
    signature_name: '',
  });

  const fetchExistingApplication = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/application/my`);
      if (response.data) {
        setExistingApp(response.data);
        setFormData(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // No existing application yet — user is filling it out for the first time
    }
  }, []);

  useEffect(() => {
    fetchExistingApplication();
  }, [fetchExistingApplication]);

  // Auto-save partial answers as a draft so admins can see in-progress applications.
  // Skips saving if the application is already finally submitted (existingApp.is_draft === false).
  useEffect(() => {
    if (existingApp && existingApp.is_draft === false) return; // already final-submitted
    const timer = setTimeout(() => {
      axios.post(`${API}/application/draft`, formData).catch(() => {
        // Draft auto-save skipped silently — will retry on next change
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [formData, existingApp]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(i => i !== value)
        : [...prev[field], value]
    }));
  };

  const handleQualityChange = (index, value) => {
    const newQualities = [...formData.three_qualities];
    newQualities[index] = value;
    setFormData(prev => ({ ...prev, three_qualities: newQualities }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('photo_base64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!formData.agree_confidentiality || !formData.agree_disclaimer || !formData.agree_non_discrimination) {
      toast.error('Please agree to all statements before submitting');
      return;
    }
    
    if (!formData.signature_name.trim()) {
      toast.error('Please provide your signature (type your full name)');
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = existingApp ? `${API}/application/update` : `${API}/application/submit`;
      const method = existingApp ? 'put' : 'post';
      
      await axios[method](endpoint, formData);
      updateUser({ application_status: 'submitted' });
      toast.success('Application submitted successfully! We will contact you within 72 hours.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 2));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const stepInfo = [
    { icon: User, title: "Basic Information", subtitle: "Tell us about yourself" },
    { icon: ShieldCheck, title: "Agreement", subtitle: "Final acknowledgement" }
  ];

  const relationshipTypes = [
    'Serious Relationship',
    'Long-term Relationship', 
    'Companionship',
    'Marriage-minded',
    'Open to possibilities'
  ];

  const personalityTraits = [
    'Outgoing', 'Reserved', 'Playful', 'Romantic', 
    'Spiritual', 'Ambitious', 'Family-Oriented', 'Adventurous',
    'Thoughtful', 'Humorous', 'Caring', 'Independent'
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8 animate-fade-in">
            {/* Photo Upload */}
            <div className="form-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="gold-accent"></div>
                <span className="form-label mb-0">Profile Photo</span>
              </div>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 rounded-lg bg-pink-50 flex items-center justify-center overflow-hidden border-2 border-dashed border-pink-200">
                  {formData.photo_base64 ? (
                    <img src={formData.photo_base64} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Camera weight="light" className="w-12 h-12 text-pink-300" />
                  )}
                </div>
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="max-w-xs"
                    data-testid="photo-upload-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    You provide at least 2 clear, up-to-date pictures of yourself (face pic and full body pic)
                  </p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-accent"></div>
                <span className="form-label mb-0">Basic Information</span>
              </div>
              <p className="text-xs text-primary mb-4 italic">Must be 21 years and older</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="form-label">Preferred Name / Nickname</Label>
                  <Input
                    placeholder="What should we call you?"
                    value={formData.preferred_name}
                    onChange={(e) => handleChange('preferred_name', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="form-label">Phone Number</Label>
                  <Input
                    placeholder="(XXX) XXX-XXXX"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-1">
                  <Label className="form-label">Height</Label>
                  <Input
                    placeholder="e.g., 5'10''"
                    value={formData.height}
                    onChange={(e) => handleChange('height', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            {/* Agreement Header */}
            <div className="text-center border-b border-pink-200 pb-6">
              <h2 className="font-serif text-2xl text-gray-800 mb-2">LINK, LAUGH & LOVE MATCHMAKING</h2>
              <h3 className="text-lg font-medium text-primary">CLIENT SERVICE AGREEMENT</h3>
            </div>

            <p className="text-muted-foreground text-sm leading-relaxed">
              This Agreement is entered into between Link, Laugh & Love Services and Client upon purchase of any service.
            </p>

            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-primary font-medium text-sm">
                YOU MUST BE 21+ YEARS OLD TO APPLY FOR LINK, LAUGH & LOVE MATCHMAKING SERVICES
              </p>
            </div>

            {/* Section 1: Services */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">1. SERVICES</h4>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Link, Laugh & Love provides personalized matchmaking and relationship advice & support. 
                Services are confidential, selective and customized. Link, Laugh & Love reserves the right 
                to refuse or terminate service at its discretion.
              </p>
            </div>

            {/* Section 2: Payment Terms */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">2. PAYMENT TERMS</h4>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Fees must be paid in full before services begin. <strong>Fees are NON-REFUNDABLE.</strong> Client 
                agrees not to dispute or reverse charges once services are purchased.
              </p>
            </div>

            {/* Section 3: No Guarantee */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">3. NO GUARANTEE</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                Clients understand matchmaking is subjective. Link, Laugh & Love <strong>DOES NOT GUARANTEE:</strong>
              </p>
              <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 ml-4">
                <li>Matches</li>
                <li>Relationships</li>
                <li>Compatibility</li>
                <li>Marriage</li>
                <li>Emotional connection</li>
              </ul>
            </div>

            {/* Section 4: Client Responsibilities */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">4. CLIENT RESPONSIBILITIES</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">Client agrees to:</p>
              <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 ml-4">
                <li>Provide truthful information</li>
                <li>Act respectfully and safely</li>
                <li>Participate actively</li>
                <li>Maintain communication</li>
              </ul>
            </div>

            {/* Section 5: Termination */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">5. TERMINATION</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                Link, Laugh & Love may terminate services immediately for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 ml-4">
                <li>Harassment or unsafe behavior</li>
                <li>Dishonesty</li>
                <li>Non-cooperation</li>
                <li>Policy violation</li>
              </ul>
            </div>

            {/* Section 6: Liability Waiver */}
            <div className="form-section">
              <h4 className="font-serif text-lg text-gray-800 mb-2">6. LIABILITY WAIVER</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                Link, Laugh & Love is not responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 ml-4">
                <li>Client interactions</li>
                <li>Emotional outcomes</li>
                <li>Personal decisions</li>
                <li>Actions of matches</li>
              </ul>
              <p className="text-muted-foreground text-sm leading-relaxed mt-3">
                Clients participate voluntarily and assume all risks.
              </p>
            </div>

            {/* Section 7: Agreement Acceptance */}
            <div className="form-section bg-pink-50 rounded-lg p-6 border border-pink-200">
              <h4 className="font-serif text-lg text-gray-800 mb-3">7. AGREEMENT ACCEPTANCE</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                By signing below or purchasing services, client confirms they have read, understood and agreed to 
                <strong> ALL terms</strong>. Your privacy is protected and your information remains confidential. 
                Link, Laugh & Love does not conduct client background check/information. False information or 
                misconduct may result in immediate termination without refund. <strong>No refunds will be issued.</strong>
              </p>

              {/* Checkboxes */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="agree_terms" 
                    checked={formData.agree_confidentiality}
                    onCheckedChange={(checked) => handleChange('agree_confidentiality', checked)}
                  />
                  <Label htmlFor="agree_terms" className="text-sm cursor-pointer">
                    I have read, understood and agree to ALL terms of this Client Service Agreement
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="agree_no_refund" 
                    checked={formData.agree_disclaimer}
                    onCheckedChange={(checked) => handleChange('agree_disclaimer', checked)}
                  />
                  <Label htmlFor="agree_no_refund" className="text-sm cursor-pointer">
                    I understand that fees are NON-REFUNDABLE and Link, Laugh & Love does not guarantee matches, 
                    relationships, compatibility, marriage or emotional connection
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="agree_21plus" 
                    checked={formData.agree_non_discrimination}
                    onCheckedChange={(checked) => handleChange('agree_non_discrimination', checked)}
                  />
                  <Label htmlFor="agree_21plus" className="text-sm cursor-pointer">
                    I confirm that I am 21 years of age or older
                  </Label>
                </div>
              </div>

              {/* E-Signature Section */}
              <div className="border-t border-pink-200 pt-6 space-y-4">
                <div>
                  <Label className="form-label">Client Name (Print)</Label>
                  <Input
                    placeholder="Enter your full legal name"
                    value={formData.signature_name}
                    onChange={(e) => handleChange('signature_name', e.target.value)}
                    className="form-input mt-2"
                  />
                </div>
                
                <div>
                  <Label className="form-label">Electronic Signature</Label>
                  <div className="relative mt-2">
                    <Input
                      placeholder="Type your full name as your electronic signature"
                      value={formData.signature_name}
                      onChange={(e) => handleChange('signature_name', e.target.value)}
                      className="form-input font-script text-lg italic"
                      style={{ fontFamily: "'Dancing Script', cursive" }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    By typing your name above, you are electronically signing this agreement
                  </p>
                </div>

                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>Date: <strong>{new Date().toLocaleDateString()}</strong></span>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-white rounded-lg p-6 border border-pink-200">
              <div className="flex items-center gap-3 mb-4">
                <HandHeart weight="fill" className="w-6 h-6 text-primary" />
                <h3 className="font-serif text-lg text-gray-800">What Happens Next?</h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                  Your dedicated matchmaker will contact you within 72 hours
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                  We will schedule your personalized consultation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                  We will begin crafting your custom match profile
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-4 h-4 text-primary" />
                  You will start connecting with carefully selected, compatible singles
                </li>
              </ul>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p className="font-medium text-primary">Link, Laugh & Love Matchmaking Team</p>
              <p className="mt-2">Phone: (810) 799-1060</p>
              <p>Email: LinkLaughLoveMatch@gmail.com</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <div className="py-12 md:py-16 rose-gradient min-h-screen">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="script-font text-3xl text-primary mb-2">Link, Laugh & Love</h1>
            <h2 className="font-serif text-2xl text-gray-800">Matchmaking Application</h2>
            <p className="text-muted-foreground text-sm mt-2">Your journey toward real love begins here</p>
          </div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {stepInfo.map((s, i) => (
                <div key={s.title} className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      i + 1 === step
                        ? 'bg-primary text-white shadow-lg'
                        : i + 1 < step
                        ? 'bg-green-500 text-white'
                        : 'bg-pink-100 text-pink-400'
                    }`}
                  >
                    {i + 1 < step ? <CheckCircle weight="fill" className="w-5 h-5" /> : <s.icon className="w-4 h-4 md:w-5 md:h-5" />}
                  </div>
                  <span className={`text-xs mt-2 hidden md:block text-center ${i + 1 === step ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    {s.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="h-2 bg-pink-100 rounded-full">
              <div 
                className="h-full bg-gradient-to-r from-primary to-pink-400 rounded-full transition-all duration-500"
                style={{ width: `${((step - 1) / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Section Header */}
            <div className="bg-gradient-to-r from-primary/10 to-pink-50 p-6 border-l-4 border-l-primary">
              <h2 className="font-serif text-2xl text-gray-800">{stepInfo[step - 1].title}</h2>
              <p className="text-muted-foreground text-sm">{stepInfo[step - 1].subtitle}</p>
            </div>

            {/* Form Content */}
            <div className="p-6 md:p-8">
              {renderStep()}

              {/* Navigation */}
              <div className="flex justify-between mt-10 pt-6 border-t border-pink-100">
                {step > 1 ? (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={prevStep}
                    className="btn-secondary"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}

                {step < 2 ? (
                  <Button 
                    type="button" 
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading || !formData.agree_confidentiality || !formData.agree_disclaimer || !formData.agree_non_discrimination || !formData.signature_name.trim()}
                    className="btn-primary"
                  >
                    {loading ? (
                      <Spinner className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Heart weight="fill" className="w-4 h-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="text-center mt-8 text-sm text-muted-foreground">
            <p>Questions? Contact us at <a href="mailto:LinkLaughLoveMatch@gmail.com" className="text-primary hover:underline">LinkLaughLoveMatch@gmail.com</a></p>
            <p>or call <a href="tel:8107991060" className="text-primary hover:underline">(810) 799-1060</a></p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ApplicationForm;
